// "Recently played" feed — patches with at least one play in the last
// 24h, ordered by most-recent play time, deduped by patch.
//
// patch_plays is publicly SELECT-able (no PII in the rows), so this
// is a direct PostgREST query rather than a Worker round-trip.
//
// Implementation note: PostgREST doesn't support DISTINCT ON, so we
// over-fetch the recent log and dedupe client-side. The 24h window +
// limit-200 cap puts a hard ceiling on the work.

import { getSupabase } from '../auth/client';

export type RecentlyPlayedItem = {
  patchId: string;
  title: string;
  authorHandle: string;
  /** ISO timestamp of the most recent play we saw in the window. */
  lastPlayedAt: string;
  /** Plays observed in the window — not the all-time count. */
  recentPlays: number;
};

const WINDOW_HOURS = 24;
const RAW_LIMIT = 200;
const RESULT_LIMIT = 12;

export async function listRecentlyPlayed(): Promise<RecentlyPlayedItem[]> {
  const supabase = getSupabase();
  if (!supabase) return [];

  const since = new Date(Date.now() - WINDOW_HOURS * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('patch_plays')
    .select(
      'patch_id, played_at, patches!inner(title, visibility, profiles!inner(handle, display_name))',
    )
    // RLS lets the public SELECT through; we still need the join filter
    // to skip plays whose parent patch was made private after the play
    // was recorded.
    .eq('patches.visibility', 'public')
    .gte('played_at', since)
    .order('played_at', { ascending: false })
    .limit(RAW_LIMIT);
  if (error) throw new Error(`recently played fetch failed: ${error.message}`);

  type PlayRow = {
    patch_id: string;
    played_at: string;
    patches?: {
      title?: string;
      profiles?: { handle?: string | null; display_name?: string | null } | null;
    } | null;
  };

  const seen = new Map<string, RecentlyPlayedItem>();
  for (const row of (data ?? []) as PlayRow[]) {
    const existing = seen.get(row.patch_id);
    if (existing) {
      existing.recentPlays += 1;
      continue;
    }
    // Patch was deleted or made private between when this play was
    // recorded and when we read it back. Drop the play silently —
    // counting it would inflate a stat we can't attribute to a row
    // the viewer can actually visit.
    if (!row.patches?.title) continue;
    seen.set(row.patch_id, {
      patchId: row.patch_id,
      title: row.patches.title,
      authorHandle:
        row.patches.profiles?.handle ?? row.patches.profiles?.display_name ?? 'anon',
      lastPlayedAt: row.played_at,
      recentPlays: 1,
    });
    if (seen.size >= RESULT_LIMIT) break;
  }
  return Array.from(seen.values());
}
